/**
 * Authentication Service - Handles all API interactions related to authentication
 */

import { post, get, put } from '../utils/apiUtils';
import { setAuthToken, setUserData, removeAuthToken, removeUserData, setCompanyData } from '../utils/authUtils';

// API endpoints - no need to include BASE_URL as axios already has it in baseURL
const AUTH_ENDPOINT = `/api/v1/authentication`;
const PERS_ENDPOINT = `/api/v1/persistence`

/**
 * Login user using username and password
 * @param {string} username - User username
 * @param {string} password - User password
 * @returns {Promise<Object>} User data and token
 */
export const login = async (username, password) => {
  try {
    // Encrypt code and secret before sending to API
    const encryptedCode = (username);
    const encryptedSecret = (password);
    
    const response = await post(`${AUTH_ENDPOINT}/login`, { 
      username: encryptedCode, 
      password: encryptedSecret,
    });
    
    // Store auth token and user data
    if (response.access_token) {
      setAuthToken(`Bearer ${response.access_token}`);
      try {
        const tenants = await getCompany();
        setCompanyData(JSON.stringify(tenants));
      } catch (error) {
        console.warn('Failed to fetch company data:', error);
        setCompanyData(JSON.stringify([]));
      }
      // setUserData(response.user || { username });
    }
    
    return response;
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
};

/**
 * Generate OAuth2 token using password grant
 * @param {string} username - User username
 * @param {string} password - User password
 * @param {string} scope - Optional scope
 * @returns {Promise<Object>} Token response
 */
export const generateToken = async (username, password, scope = '') => {
  try {
    const formData = new URLSearchParams();
    formData.append('grant_type', 'password');
    formData.append('username', username);
    formData.append('password', password);
    formData.append('scope', scope);

    const response = await fetch(`${AUTH_ENDPOINT}/generate_token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Token generation failed: ${response.statusText}`);
    }

    const tokenData = await response.json();
    
    // Store auth token
    if (tokenData.access_token) {
      setAuthToken(`Bearer ${tokenData.access_token}`);
    }
    
    return tokenData;
  } catch (error) {
    console.error('Token generation failed:', error);
    throw error;
  }
};

/**
 * Get token using client credentials
 * @param {string} client_id - Client ID
 * @param {string} client_secret - Client secret
 * @returns {Promise<Object>} Token response
 */
export const getClientToken = async (client_id, client_secret) => {
  try {
    const response = await post(`${AUTH_ENDPOINT}/token`, { client_id, client_secret });
    
    // Store auth token
    if (response.access_token) {
      setAuthToken(`Bearer ${response.access_token}`);
    }
    
    return response;
  } catch (error) {
    console.error('Client token generation failed:', error);
    throw error;
  }
};

/**
 * Register new user
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} Registration response
 */
export const register = async (userData) => {
  try {
    const response = await post(`${AUTH_ENDPOINT}/register`, userData);
    
    return response;
  } catch (error) {
    console.error('Registration failed:', error);
    throw error;
  }
};

/**
 * Create new application
 * @param {Object} appData - Application data
 * @returns {Promise<Object>} Application creation response
 */
export const createApplication = async (appData) => {
  try {
    const response = await post(`${AUTH_ENDPOINT}/create_application`, appData);
    
    return response;
  } catch (error) {
    console.error('Application creation failed:', error);
    throw error;
  }
};

/**
 * Create new company
 * @param {Object} companyData - Company data
 * @returns {Promise<Object>} Company creation response
 */
export const upadateCompany = async (companyData) => {
  try {
    const response = await post(`${AUTH_ENDPOINT}/select_user_tenant`, companyData);
     const userData = {
        ...response?.token_info,
        ...response.user_context,
        roles: response.user_context.roles,
        groups: response.user_context.groups

      }
    if(response?.token_info) {
      setAuthToken(`Bearer ${response.token_info.access_token}`);
      setUserData(userData);

    }
    
    return userData;
  } catch (error) {
    console.error('Company creation failed:', error);
    throw error;
  }
};

/**
 * create company
 * @param {Object} companyData - Company data
 * @returns {Promise<Object>} Company creation response
 */
export const createCompany = async (companyData) => {
  try {
    const response = await post(`${AUTH_ENDPOINT}/create_company`, companyData);
    
    return response;
  } catch (error) {
    console.error('Company creation failed:', error);
    throw error;
  }
};

//  Update companyData
//  @param {Object} companyData - Company data
//  @returns {Promise<Object>} Company creation response
 
export const updateCompanyData = async (companyData) => {
  try {
    const response = await put(`${PERS_ENDPOINT}/company`, companyData);
    
    return response;
  } catch (error) {
    console.error('Company updation failed:', error);
    throw error;
  }
};

/**
 * Logout user
 * @returns {Promise<void>}
 */
export const logout = async () => {
  try {
    // No explicit logout endpoint in the API, just remove local tokens
    removeAuthToken();
    removeUserData();
  } catch (error) {
    console.error('Logout failed:', error);
    // Always remove local auth data even if API call fails
    removeAuthToken();
    removeUserData();
  }
};


/**
 * Logout user
 * @returns {Promise<void>}
 */

export const getCompany = async () => {
    try {
    const response = await get(`${AUTH_ENDPOINT}/get_user_tenants`);
    
    return response;
  } catch (error) {
    console.error('Registration failed:', error);
    throw error;
  }
}

/**
 * Get Users
 * @returns {Promise<Object>} Registration response
 */
export const fetchUsers = async () => {
  try {
    const response = await get(`${AUTH_ENDPOINT}/get_users`);
    
    return response;
  } catch (error) {
    console.error('Error while fetching users:', error);
    throw error;
  }
};

/**
 * Get Users
 * @returns {Promise<Object>} Registration response
 */
export const fetchUsersGropus = async () => {
  try {
    const response = await get(`${PERS_ENDPOINT}/user_group`);
    
    return response;
  } catch (error) {
    console.error('Error while fetching users:', error);
    throw error;
  }
};



export default {
  login,
  generateToken,
  getClientToken,
  register,
  createApplication,
  createCompany,
  logout
};
